import { useId } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const inputCls =
  "border-black/15 bg-white text-neutral-900 placeholder:text-neutral-500 hover:border-black/25 dark:border-black/15 dark:bg-white dark:text-neutral-900 dark:placeholder:text-neutral-500";

/** Labelled numeric input used by the calculator tools. */
export function NumField({
  id,
  label,
  value,
  onChange,
  placeholder,
  suffix,
}: {
  id?: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  suffix?: string;
}) {
  const gen = useId();
  const domId = id ?? gen;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={domId} className="text-muted-foreground">
        {label}
      </Label>
      <div className="flex items-center gap-2">
        <Input
          id={domId}
          type="number"
          inputMode="decimal"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        />
        {suffix ? (
          <span className="shrink-0 text-sm text-muted-foreground">
            {suffix}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** Segmented single-choice control (sex, activity, formula…). */
export function ChoiceField<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="space-y-1.5">
      <span className="block text-sm text-muted-foreground">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            aria-pressed={value === o.value}
            onClick={() => onChange(o.value)}
            className={
              value === o.value
                ? "sci-result-zone sci-result rounded-md px-3 py-1.5 text-sm font-semibold"
                : "sci-panel rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            }
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
